import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import TextField from "@mui/material/TextField";
import * as Yup from "yup";
import { IoMdDownload } from "react-icons/io";
import CustomModal from "../Modal";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import { useFormik } from "formik";
import { getCustomerDropdownDataApiCall } from "../../../Api/api";

const initialValues = {
   startDate: "",
   endDate: "",
   customerNames: [],
};

const validationSchema = Yup.object().shape({
   startDate: Yup.date()
      .required("Start date is required")
      .typeError("Invalid date format"),
   endDate: Yup.date()
      .required("End date is required")
      .typeError("Invalid date format")
      .min(Yup.ref("startDate"), "End date must be later than or equal to Start date"),
});

const DownloadReportButton = ({
   title = "Download Report",
   modal_title,
   onDownloadReport,
   confirmPopup,
   needCustomerFilter,
}) => {
   const [customerOptions, setCustomerOptions] = useState([]);
   const [selectAll, setSelectAll] = useState(false);
   const [searchQuery, setSearchQuery] = useState("");
   const finalModalTitle = modal_title || "Choose Dates";

   const hndleCloseDateModal = () => {
      resetForm();
      setFieldValue("customerNames", []);
      setSelectAll(false);
      setSearchQuery("");
      confirmPopup?.onFalse();
   };

   const handleGetReportData = (formData) => {
      const data = {
         from_date: formData?.startDate,
         to_date: formData?.endDate,
         model_id: 11,
         action_id: 3,
         //  _method: "PATCH",
         ...formData,
         // For backward compatibility, also send customerName if only one selected
         customerName:
            formData?.customerNames?.length === 1
               ? formData?.customerNames[0]
               : undefined,
      };

      onDownloadReport &&
         onDownloadReport(data, () => {
            hndleCloseDateModal();
         });
   };

   const getCustomerDropdown = async () => {
      try {
         const res = await getCustomerDropdownDataApiCall();
         const options = res?.data?.res?.map?.((item) => ({
            label: item?.name,
            value: item?.id,
         }));
         setCustomerOptions(options);
      } catch (error) {
         //
      }
   };

   const formik = useFormik({
      initialValues,
      validationSchema,
      enableReinitialize: true,
      onSubmit: handleGetReportData,
   });

   const {
      errors,
      values,
      touched,
      getFieldProps,
      handleSubmit,
      setFieldValue,
      resetForm,
   } = formik;

   useEffect(() => {
      if (needCustomerFilter) {
         getCustomerDropdown();
      }
   }, [needCustomerFilter]);

   // Filter customers based on search query
   const filteredCustomerOptions = customerOptions.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()),
   );

   // Handle Select All checkbox (selects only filtered options)
   const handleSelectAll = (event) => {
      const checked = event.target.checked;
      setSelectAll(checked);
      if (checked) {
         const allNames = filteredCustomerOptions.map((option) => option.label);
         // Merge with existing selections (keep previously selected that are not in current filter)
         const currentNames = values.customerNames || [];
         const mergedNames = [...new Set([...currentNames, ...allNames])];
         setFieldValue("customerNames", mergedNames);
      } else {
         // Remove only the filtered options from selection
         const filteredNames = filteredCustomerOptions.map((option) => option.label);
         const remainingNames = (values.customerNames || []).filter(
            (name) => !filteredNames.includes(name),
         );
         setFieldValue("customerNames", remainingNames);
      }
   };

   // Handle individual customer checkbox
   const handleCustomerChange = (customerName) => {
      const currentNames = values.customerNames || [];
      let newNames;
      if (currentNames.includes(customerName)) {
         newNames = currentNames.filter((name) => name !== customerName);
      } else {
         newNames = [...currentNames, customerName];
      }
      setFieldValue("customerNames", newNames);
      // Update selectAll state based on filtered selection
      const filteredNames = filteredCustomerOptions.map((option) => option.label);
      const allFilteredSelected = filteredNames.every((name) => newNames.includes(name));
      setSelectAll(allFilteredSelected && filteredNames.length > 0);
   };

   // Check if a customer is selected
   const isCustomerSelected = (customerName) => {
      return (values.customerNames || []).includes(customerName);
   };

   return (
      <>
         <CustomModal
            showModal={confirmPopup?.value}
            handleCloseModal={hndleCloseDateModal}
            modalSize="modal-lg"
            modalHeight="80vh"
            title={finalModalTitle}
            onSubmit={handleSubmit}
            child={
               <div className="row card-body">
                  {needCustomerFilter && (
                     <div className="col-lg-12 mb-3">
                        <label className="text-capitalize mb-2">Select Customers</label>
                        {/* Search Input */}
                        <TextField
                           fullWidth
                           size="small"
                           placeholder="Search customers..."
                           value={searchQuery}
                           onChange={(e) => setSearchQuery(e.target.value)}
                           sx={{ marginBottom: "10px" }}
                        />
                        <div
                           style={{
                              maxHeight: "200px",
                              overflowY: "auto",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              padding: "10px",
                           }}
                        >
                           <FormGroup>
                              {/* Select All Checkbox */}
                              <FormControlLabel
                                 control={
                                    <Checkbox
                                       checked={selectAll}
                                       onChange={handleSelectAll}
                                       indeterminate={
                                          values.customerNames?.length > 0 &&
                                          values.customerNames?.length <
                                             customerOptions.length
                                       }
                                       sx={{
                                          "&.Mui-checked": { color: "#1976d2" },
                                          "&.MuiCheckbox-indeterminate": {
                                             color: "#1976d2",
                                          },
                                       }}
                                    />
                                 }
                                 label={
                                    <strong>
                                       Select All{searchQuery ? ` (filtered)` : ""}
                                    </strong>
                                 }
                                 sx={{
                                    borderBottom: "1px solid #eee",
                                    marginBottom: "8px",
                                    paddingBottom: "8px",
                                 }}
                              />
                              {/* Individual Customer Checkboxes */}
                              {filteredCustomerOptions.map((option) => (
                                 <FormControlLabel
                                    key={option.value}
                                    control={
                                       <Checkbox
                                          checked={isCustomerSelected(option.label)}
                                          onChange={() =>
                                             handleCustomerChange(option.label)
                                          }
                                          sx={{ "&.Mui-checked": { color: "#1976d2" } }}
                                       />
                                    }
                                    label={option.label}
                                 />
                              ))}
                              {filteredCustomerOptions.length === 0 && (
                                 <small className="text-muted">No customers found</small>
                              )}
                           </FormGroup>
                        </div>
                        {values.customerNames?.length > 0 && (
                           <small className="text-muted">
                              {values.customerNames.length} customer(s) selected
                           </small>
                        )}
                     </div>
                  )}
                  <div className="col-lg-6">
                     <CustomDatePicker
                        require
                        label="Start date"
                        placeholder="Enter start date"
                        {...getFieldProps("startDate")}
                        errors={Boolean(touched.startDate) && Boolean(errors.startDate)}
                        message={errors.startDate}
                     />
                  </div>
                  <div className="col-lg-6">
                     <CustomDatePicker
                        require
                        label="End date"
                        placeholder="Enter end date"
                        {...getFieldProps("endDate")}
                        errors={Boolean(touched.endDate) && Boolean(errors.endDate)}
                        message={errors.endDate}
                     />
                  </div>
               </div>
            }
         />

         <Box>
            <Button
               variant="outlined"
               startIcon={<IoMdDownload />}
               onClick={confirmPopup?.onTrue}
            >
               {title}
            </Button>
         </Box>
      </>
   );
};

export default DownloadReportButton;
